import _ from 'lodash'
import React from 'react'
import { Grid, GridColumn, GridRow, Icon, List } from 'semantic-ui-react'
import 'swiper/components/navigation/navigation.min.css'
import 'swiper/components/pagination/pagination.min.css'

import SwiperCore, { Navigation, Pagination } from 'swiper/core'

import 'swiper/swiper.min.css'
import { getAreaDisplayFromMetresSquared } from '../../apis/conversionService'
import {
  NT_PLANNING_SCHEME_LINK,
  PLANNING_SERVICES_EMAIL,
  PLANNING_SERVICES_PHONE,
  PLANNING_SERVICES_PHONE_INTERNATIONAL,
  STRATEGIC_FRAMEWORK_LINK,
} from '../../environment'
import { PlanningData, StrategicPlanData } from '../planning-components/slice'

import './SchemeInformation.css'

// install Swiper modules
SwiperCore.use([Pagination, Navigation])

type props = {
  planningData: PlanningData
}

function SchemeInformation({ planningData }: props) {

  function displayLandArea() {
    return (
      <React.Fragment>
        <GridRow className="planningDetailsL1Row">
          <GridColumn width={6} className="planningDetailsFieldLabel">
            <p>Land Area :</p>
          </GridColumn>
          <GridColumn width={10} className="planningDetailsFieldValue">
            {getAreaDisplayFromMetresSquared(planningData.landArea)}
          </GridColumn>
        </GridRow>
      </React.Fragment>
    )
  }

  function displayPlanningSchemeZone() {
    return (
      <React.Fragment>
        <GridRow className="planningDetailsL1Row">
          <GridColumn width={6} className="planningDetailsFieldLabel">
            <p>Planning Scheme Zone :</p>
          </GridColumn>
          <GridColumn width={10} className="planningDetailsFieldValue">
            <p>{planningData.planningSchemeZone}</p>
          </GridColumn>
        </GridRow>
      </React.Fragment>
    )
  }

  function displayOverlays() {
    return (
      <React.Fragment>
        <GridRow className="planningDetailsL1Row">
          <GridColumn width={6} className="planningDetailsFieldLabel">
            <p>Overlays :</p>
          </GridColumn>
          <GridColumn width={10} className="planningDetailsFieldValue">
            {_.isEmpty(planningData.overlays) ? '(none found)' :
              <List bulleted className="bulletList">
                {planningData.overlays?.map((o) => {
                  return (
                    <List.Item key={o} className="bulletListItem">{o}</List.Item>
                  )
                })}
              </List>
            }
          </GridColumn>
        </GridRow>
      </React.Fragment>
    )
  }

  function displayPlans(label: string, plans: string[]) {
    return (
      <React.Fragment>
        <p className="strategicFrameworksHeader">{label}</p>
        <List bulleted className="bulletList">
          {_.isEmpty(plans) ? <List.Item key={label + 'notFound'} className="bulletListItem">(none
              found)</List.Item> :
            plans.map(p => {
              return (
                <List.Item key={p} className="bulletListItem">{p}</List.Item>
              )
            })
          }
        </List>
      </React.Fragment>
    )
  }

  function displayFrameworks(frameworks: StrategicPlanData) {
    if (_.isEmpty(frameworks)) {
      return '(none found)'
    }
    return (
      <React.Fragment>
        <p className="strategicFrameworksHeader">The following <a href={STRATEGIC_FRAMEWORK_LINK}
                                                                  className="linkUnderline" target="_blank"
                                                                  rel="noopener noreferrer">Strategic
          Frameworks</a> may apply to your land</p>
        {displayPlans('Regional Plans', frameworks.regionalPlans)}
        {displayPlans('Sub Regional Plans', frameworks.subRegionalPlans)}
        {displayPlans('Area Plans', frameworks.areaPlans)}
      </React.Fragment>
    )
  }

  function displayStrategicFrameworks() {
    return (
      <React.Fragment>
        <GridRow className="planningDetailsL1Row">
          <GridColumn width={6} className="planningDetailsFieldLabel">
            <p>Strategic Frameworks :</p>
          </GridColumn>
          <GridColumn width={10} className="planningDetailsFieldValue">
            {planningData.strategicFrameworks && displayFrameworks(planningData.strategicFrameworks)}
          </GridColumn>
        </GridRow>
      </React.Fragment>
    )
  }

  function displayInterimDevelopmentControlOrders() {
    return (
      <React.Fragment>
        <GridRow className="planningDetailsL1Row">
          <GridColumn width={6} className="planningDetailsFieldLabel">
            <p>Interim Development Control Orders :</p>
          </GridColumn>
          <GridColumn width={10} className="planningDetailsFieldValue">
            {_.isEmpty(planningData.interimDevelopmentControlOrders) ? '(none found)' :

              <List bulleted className="bulletList">
                {planningData.interimDevelopmentControlOrders?.map((o) => {
                  return (
                    <List.Item key={o} className="bulletListItem">{o}</List.Item>
                  )
                })}
              </List>
            }
          </GridColumn>
        </GridRow>
      </React.Fragment>
    )
  }

  function displayPlanningUnavailableError() {
    return (
      <React.Fragment>

        <GridColumn width={16}>
          <div className={'largePlanningError'}>
            <Icon name={'warning circle'} size={'large'}/>
            <p>Unavailable</p>
          </div>
          <p style={{ textAlign: 'center' }}>Planning Details not available online. Please contact Development
            Assessment Services
            on <a href={`mailto: ${PLANNING_SERVICES_EMAIL}`}>{PLANNING_SERVICES_EMAIL}</a> or phone: <a
              href={`tel:${PLANNING_SERVICES_PHONE_INTERNATIONAL}`}>{PLANNING_SERVICES_PHONE}</a></p>
        </GridColumn>

      </React.Fragment>
    )
  }

  function displayInternalError() {
    return (
      <React.Fragment>

        <GridColumn width={16}>
          <div className={'largePlanningError'}
               style={{ textAlign: 'center', fontWeight: 'bolder', color: 'indianred', fontSize: '3em' }}>
            <Icon name={'warning circle'} size={'large'}/>
            <p>Error</p>
          </div>
          <p style={{ textAlign: 'center' }}>Online plannings are currently unavailable. If your query is urgent
            please contact Valuation Services on <a
              href={`mailto: ${PLANNING_SERVICES_EMAIL}`}>{PLANNING_SERVICES_EMAIL}</a> or phone: <a
              href={`tel:${PLANNING_SERVICES_PHONE_INTERNATIONAL}`}>{PLANNING_SERVICES_PHONE}</a></p>
        </GridColumn>

      </React.Fragment>
    )
  }

  function displayFields() {
    if (planningData.error && planningData.errorType === 'INTERNAL_ERROR') {
      // internal error
      return (
        <React.Fragment>
          {displayInternalError()}
        </React.Fragment>
      )
    } else if (planningData.error) {
      return (
        <React.Fragment>
          {displayPlanningUnavailableError()}
        </React.Fragment>
      )
    } else if (!planningData.error) {
      return (
        <React.Fragment>
          {displayValidPlanningFields()}
        </React.Fragment>
      )
    }
  }

  function displayValidPlanningFields() {
    return (
      <React.Fragment>
        {displayLandArea()}
        {displayPlanningSchemeZone()}
        {displayOverlays()}
        {displayStrategicFrameworks()}
        {displayInterimDevelopmentControlOrders()}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <h3 className="parcelDetailsSubject"><a href={NT_PLANNING_SCHEME_LINK}
                                              className="linkUnderline" target="_blank"
                                              rel="noopener noreferrer">NT Planning
        Scheme</a> Information</h3>
      <Grid className="planningFieldsGrid" divided="vertically">
        {displayFields()}
      </Grid>
    </React.Fragment>
  )
}

export default SchemeInformation
